import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';
import SiteItemStatus from 'src/pages/Sites/components/SiteItemStatus';
import { SiteItemStatus as Status, SiteClusterProviderType } from '../../types';

const iconStyle = { color: '#0047BA' };

type Props = {
  status: Status | 'inprogress';
  label: string;
  observability?: string;
  providerType?: string;
  authType?: string;
  kubernetesVersion?: string;
  instanceType?: string;
  operatingSystem?: string;
  cloudConfigRefUid?: string;
};

type DetailFieldProps = {
  label: string;
  children: React.ReactNode;
};

function DetailField({ label, children }: DetailFieldProps) {
  return (
    <Box display="flex" flexDirection="column" gap={0.5} minWidth={120}>
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: '16px', fontWeight: 400 }}>{children}</Box>
    </Box>
  );
}

function ClusterDetails({
  status,
  label,
  observability,
  providerType,
  authType,
  kubernetesVersion,
  instanceType,
  operatingSystem,
  cloudConfigRefUid,
}: Props) {
  const [isCopied, setIsCopied] = useState('');
  const handleNavigate = () => window.open(observability);

  const handleCopy = (text?: string) => {
    navigator.clipboard.writeText(text ?? '').then(() => {
      setIsCopied(text ?? '');
    });
  };

  const showLabel =
    providerType !== SiteClusterProviderType.Spectro && label && label !== '-';

  return (
    <Box display="flex" flexDirection="column" gap={1.5} sx={{ py: 1.5, px: 0.5 }}>
      {/* Row 1: main cluster metadata */}
      <Box display="flex" flexWrap="wrap" gap={3} alignItems="flex-start">
        {providerType && (
          <DetailField label="Provider Type">
            <Typography sx={{ fontSize: '16px' }}>{providerType}</Typography>
          </DetailField>
        )}
        {authType && (
          <DetailField label="Auth Type">
            <Typography sx={{ fontSize: '16px' }}>{authType}</Typography>
          </DetailField>
        )}
        <DetailField label="Status">
          <SiteItemStatus
            value={status === 'inprogress' ? Status.InProgress : status}
          />
        </DetailField>
        {instanceType && (
          <DetailField label="Instance Type">
            <Typography sx={{ fontSize: '16px' }}>{instanceType}</Typography>
          </DetailField>
        )}
        {kubernetesVersion && (
          <DetailField label="Kubernetes Version">
            <Typography sx={{ fontSize: '16px' }}>{kubernetesVersion}</Typography>
          </DetailField>
        )}
        {operatingSystem && (
          <DetailField label="OS">
            <Typography sx={{ fontSize: '16px' }}>{operatingSystem}</Typography>
          </DetailField>
        )}

      </Box>

      {/* Row 2: label with copy and monitoring dashboard link */}
      {(showLabel || observability) && (
        <Box display="flex" alignItems="center" gap={4} alignSelf="flex-end">
          {showLabel && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                Label
              </Typography>
              <Typography
                sx={{ fontSize: '16px' }}
                title={label}
              >
                {label}
              </Typography>
              <IconButton
                size="small"
                title={isCopied === label ? 'Copied to clipboard' : 'Copy'}
                onClick={() => handleCopy(label)}
                sx={{ p: 0.25 }}
              >
                <ContentCopyIcon sx={{ ...iconStyle, fontSize: '16px' }} />
              </IconButton>
            </Box>
          )}
          {observability && (
            <Link
              onClick={handleNavigate}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', fontSize: '16px', fontWeight: 400 }}
            >
              Monitoring dashboard
              <OpenInNewIcon sx={{ fontSize: '16px' }} />
            </Link>
          )}
        </Box>
      )}
    </Box>
  );
}

export default ClusterDetails;
